/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Tracker v2.1.1 - Página de Tracking GPS Profesional
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file frontend/pages/TrackingPage.tsx
 * @version 2.1.1 - INTRUSION ALERT SYSTEM
 * @description Sistema de tracking con alertas de intrusión en tiempo real
 * @author DevOps/RedTeam @LINUXCORP l33t h4x0r Security Team
 * @license Commercial
 * 
 * FIXES v2.1.1:
 * ✅ Issue 1: Fingerprint persistente (localStorage)
 * ✅ Issue 2: Intruso NUNCA ve mapa - validación antes de startTracking()
 * ✅ Issue 3: Sistema de alertas de intrusión con foto + GPS
 * 
 * FLUJO DE SEGURIDAD v2.1.1:
 * 1. Usuario abre link → Pantalla de permisos
 * 2. Usuario acepta ubicación → Captura GPS inicial
 * 3. Validación con fingerprint + GPS
 * 4. SI INTRUSO → Solicita cámara → Captura foto → Alerta WhatsApp
 * 5. SI VÁLIDO → Inicia tracking continuo
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt,
  faSync,
  faExclamationTriangle,
  faCheckCircle,
  faLock,
  faBan,
  faInfoCircle,
  faShareAlt,
  faBars,
  faHome,
  faSignOutAlt,
  faRobot,
  faTimes,
  faDownload,
  faSatelliteDish,
  faMap,
  faGlobe,
  faMountain,
  faShieldAlt,
  faCamera
} from '@fortawesome/free-solid-svg-icons';

// @ts-ignore
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURACIÓN
// ═══════════════════════════════════════════════════════════════════════════

const CONFIG = {
  API_BASE_URL: 'https://backend.anubisai.net/api/tracking/wasp',
  USE_MOCK_LOCATION: false, // ⚠️ CAMBIAR A false EN PRODUCCIÓN
  MOCK_LOCATION: {
    latitude: -33.693021,
    longitude: -71.094888,
    accuracy: 10,
    speed: 0
  },
  CHATBOT_ENABLED: true,
  UPDATE_INTERVAL: 5000,
  PWA_ENABLED: true,
  INTRUSION_ALERT_ENABLED: true // 🆕 Sistema de alertas
};

// ═══════════════════════════════════════════════════════════════════════════
// INTERFACES
// ═══════════════════════════════════════════════════════════════════════════

interface LocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
  speed: number | null;
  timestamp: string;
}

interface TrackingState {
  active: boolean;
  error: string | null;
  updateCount: number;
  lastUpdate: string | null;
  tokenValid: boolean;
  tokenValidated: boolean;
  validating: boolean;
  sending: boolean;
  permissionStatus: 'granted' | 'denied' | 'prompt' | 'unknown';
  permissionRequested: boolean; // 🆕 Si ya pidió permisos
  connectionStatus: 'connected' | 'disconnected' | 'waiting';
  isIntruder: boolean;
  capturingPhoto: boolean; // 🆕 Si está capturando foto
  alertSent: boolean; // 🆕 Si ya envió alerta
}

type MapType = 'street' | 'satellite' | 'terrain';

// ═══════════════════════════════════════════════════════════════════════════
// DEVICE FINGERPRINTING
// ═══════════════════════════════════════════════════════════════════════════

const generateDeviceFingerprint = (): string => {
  const stored = localStorage.getItem('wasp_device_fp');
  if (stored) {
    console.log('[Fingerprint] Usando fingerprint persistido');
    return stored;
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Fingerprint', 2, 2);
  }
  const canvasData = canvas.toDataURL();
  
  const fingerprint = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    canvasFingerprint: canvasData.substring(0, 100),
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
    deviceMemory: (navigator as any).deviceMemory || 0
  };

  const fingerprintString = JSON.stringify(fingerprint);
  
  let hash = 0;
  for (let i = 0; i < fingerprintString.length; i++) {
    const char = fingerprintString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  const newFingerprint = Math.abs(hash).toString(36);
  localStorage.setItem('wasp_device_fp', newFingerprint);
  console.log('[Fingerprint] Nuevo fingerprint generado y guardado');
  
  return newFingerprint;
};

const getDeviceInfo = () => {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const TrackingPage: React.FC = () => {
  const [token, setToken] = useState<string | null>(null);
  const [deviceFingerprint, setDeviceFingerprint] = useState<string>(() => generateDeviceFingerprint());
  const [tracking, setTracking] = useState<TrackingState>({
    active: false,
    error: null,
    updateCount: 0,
    lastUpdate: null,
    tokenValid: false,
    tokenValidated: false,
    validating: false,
    sending: false,
    permissionStatus: 'unknown',
    permissionRequested: false, // 🆕 Inicia en false
    connectionStatus: 'waiting',
    isIntruder: false,
    capturingPhoto: false, // 🆕
    alertSent: false // 🆕
  });
  const [currentLocation, setCurrentLocation] = useState<LocationData | null>(null);
  const [initialLocation, setInitialLocation] = useState<LocationData | null>(null); // 🆕
  const [menuOpen, setMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [mapType, setMapType] = useState<MapType>('street');
  const [showPWAPrompt, setShowPWAPrompt] = useState(false);

  const watchIdRef = useRef<number | null>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const deferredPromptRef = useRef<any>(null);
  
  const location = useLocation();
  const { token: pathToken } = useParams<{ token: string }>();

  // ═══════════════════════════════════════════════════════════════════════════
  // PWA - INSTALACIÓN
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    if (!CONFIG.PWA_ENABLED) return;

    const handleBeforeInstall = (e: any) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setShowPWAPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    if (window.matchMedia('(display-mode: standalone)').matches) {
      console.log('[PWA] App instalada como aplicación');
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallPWA = async () => {
    if (!deferredPromptRef.current) return;

    deferredPromptRef.current.prompt();
    const { outcome } = await deferredPromptRef.current.userChoice;
    
    console.log(`[PWA] Usuario ${outcome === 'accepted' ? 'aceptó' : 'rechazó'} la instalación`);
    
    deferredPromptRef.current = null;
    setShowPWAPrompt(false);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // BACKGROUND PERSISTENCE
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    let wakeLock: any = null;

    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await (navigator as any).wakeLock.request('screen');
          console.log('[Wake Lock] Activado - pantalla permanecerá activa');
        }
      } catch (err) {
        console.warn('[Wake Lock] No disponible:', err);
      }
    };

    if (tracking.active) {
      requestWakeLock();
    }

    const handleVisibilityChange = () => {
      if (wakeLock !== null && document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (wakeLock !== null) {
        wakeLock.release();
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [tracking.active]);

  // ═══════════════════════════════════════════════════════════════════════════
  // EFECTOS PRINCIPALES
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const urlToken = pathToken;
    if (urlToken) {
      setToken(urlToken);
      console.log('[Tracking] Token cargado desde URL');
      // 🆕 NO validar aquí - esperar permisos primero
    } else {
      setTracking(prev => ({
        ...prev,
        error: 'Token no encontrado en URL',
        tokenValid: false,
        tokenValidated: true
      }));
    }

    return () => {
      stopTracking();
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, [pathToken]);

  useEffect(() => {
    if (tracking.validating || tracking.isIntruder) {
      return;
    }
    
    if (currentLocation && mapContainerRef.current && !mapRef.current) {
      initMap();
    }
    
    if (currentLocation && mapRef.current) {
      updateMap();
    }
  }, [currentLocation, tracking.validating, tracking.isIntruder]);

  useEffect(() => {
    if (mapRef.current) {
      changeMapType();
    }
  }, [mapType]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 🆕 SOLICITUD DE PERMISOS
  // ═══════════════════════════════════════════════════════════════════════════

  const handleRequestPermissions = async () => {
    if (!token) {
      setTracking(prev => ({
        ...prev,
        error: 'Token no disponible',
        permissionRequested: true
      }));
      return;
    }

    try {
      console.log('[Permissions] Solicitando acceso a ubicación...');
      
      if (CONFIG.USE_MOCK_LOCATION) {
        // MODO DESARROLLO - Mock location
        console.log('[Permissions] 🔧 Usando ubicación simulada');
        
        const mockLocation: LocationData = {
          ...CONFIG.MOCK_LOCATION,
          timestamp: new Date().toISOString()
        };
        
        setInitialLocation(mockLocation);
        setTracking(prev => ({
          ...prev,
          permissionRequested: true,
          permissionStatus: 'granted'
        }));
        
        // Validar con ubicación simulada
        validateToken(token, mockLocation);
        return;
      }

      // PRODUCCIÓN - Ubicación real
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        );
      });
      
      const location: LocationData = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        speed: position.coords.speed,
        timestamp: new Date().toISOString()
      };
      
      console.log('[Permissions] ✅ Ubicación obtenida:', location);
      
      setInitialLocation(location);
      setTracking(prev => ({
        ...prev,
        permissionRequested: true,
        permissionStatus: 'granted'
      }));
      
      // AHORA SÍ validar token con ubicación
      validateToken(token, location);
      
    } catch (error: any) {
      console.error('[Permissions] Error:', error);
      
      setTracking(prev => ({
        ...prev,
        permissionRequested: true,
        permissionStatus: 'denied',
        error: 'Se requiere acceso a ubicación para continuar'
      }));
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // FUNCIONES MAPA
  // ═══════════════════════════════════════════════════════════════════════════

  const getMapTileLayer = () => {
    switch (mapType) {
      case 'satellite':
        return {
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          attribution: '© Esri'
        };
      case 'terrain':
        return {
          url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
          attribution: '© OpenTopoMap'
        };
      default:
        return {
          url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          attribution: '© OpenStreetMap'
        };
    }
  };

  const initMap = () => {
    if (!currentLocation || !mapContainerRef.current) return;

    const waspIcon = (L as any).divIcon({
      html: `<div style="font-size: 28px; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));">${getWaspSVG()}</div>`,
      className: 'wasp-marker',
      iconSize: [30, 30],
      iconAnchor: [15, 15]
    });

    mapRef.current = (L as any).map(mapContainerRef.current, {
      zoomControl: false
    }).setView([currentLocation.latitude, currentLocation.longitude], 16);

    (L as any).control.zoom({ position: 'bottomright', zoomInTitle: 'Zoom in', zoomOutTitle: 'Zoom out', top: '-30px' }).addTo(mapRef.current);

    const tileConfig = getMapTileLayer();
    (L as any).tileLayer(tileConfig.url, {
      attribution: tileConfig.attribution,
      maxZoom: 19
    }).addTo(mapRef.current);

    markerRef.current = (L as any).marker([currentLocation.latitude, currentLocation.longitude], {
      icon: waspIcon
    })
      .addTo(mapRef.current)
      .bindPopup('📍 Tu ubicación actual');

    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 100);
  };

  const changeMapType = () => {
    if (!mapRef.current) return;

    mapRef.current.eachLayer((layer: any) => {
      if (layer instanceof (L as any).TileLayer) {
        mapRef.current.removeLayer(layer);
      }
    });

    const tileConfig = getMapTileLayer();
    (L as any).tileLayer(tileConfig.url, {
      attribution: tileConfig.attribution,
      maxZoom: 19
    }).addTo(mapRef.current);
  };

  const updateMap = () => {
    if (!currentLocation || !mapRef.current) return;

    const newLatLng = (L as any).latLng(currentLocation.latitude, currentLocation.longitude);

    if (markerRef.current) {
      markerRef.current.setLatLng(newLatLng);
    }

    mapRef.current.setView(newLatLng, mapRef.current.getZoom());
  };

  useEffect(() => {
    if (!mapRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    });

    if (mapContainerRef.current) {
      resizeObserver.observe(mapContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // 🆕 VALIDACIÓN CON UBICACIÓN
  // ═══════════════════════════════════════════════════════════════════════════

  const validateToken = async (tokenToValidate: string, location: LocationData) => {
    setTracking(prev => ({ ...prev, validating: true }));
    
    try {
      console.log('[Tracking] Validando token con ubicación:', {
        lat: location.latitude.toFixed(6),
        lon: location.longitude.toFixed(6),
        fingerprint: deviceFingerprint.substring(0, 20) + '...'
      });
      
      const params = new URLSearchParams({
  deviceFingerprint: deviceFingerprint,
  latitude: location.latitude.toString(),
  longitude: location.longitude.toString(),
  accuracy: location.accuracy.toString(),
  userAgent: getDeviceInfo().userAgent,
  platform: getDeviceInfo().platform,
  language: getDeviceInfo().language,
  screenResolution: getDeviceInfo().screenResolution,
  timezone: getDeviceInfo().timezone
});

const response = await axios.get(
  `${CONFIG.API_BASE_URL}/validate/${tokenToValidate}?${params}`,
  { 
    timeout: 10000,
    headers: { 'Content-Type': 'application/json' }
  }
);

      if (response.data.success && response.data.valid) {
        if (response.data.isIntruder) {
          console.log('[Tracking] 🚨 INTRUSO detectado - escalando a cámara');
          
          setTracking(prev => ({
            ...prev,
            tokenValid: false,
            tokenValidated: true,
            validating: false,
            isIntruder: true,
            capturingPhoto: true,
            error: '🚨 Dispositivo no autorizado',
            connectionStatus: 'disconnected'
          }));
          
          // 🆕 ESCALAR A CÁMARA
          if (CONFIG.INTRUSION_ALERT_ENABLED) {
            captureIntruderPhoto(location);
          } else {
            setTracking(prev => ({ ...prev, capturingPhoto: false }));
          }
          return;
        }
        
        console.log('[Tracking] ✅ Token válido - iniciando tracking');
        setTracking(prev => ({
          ...prev,
          tokenValid: true,
          tokenValidated: true,
          validating: false,
          isIntruder: false,
          error: null,
          connectionStatus: 'connected'
        }));
        
        setTimeout(() => {
          startTracking();
        }, 100);
        
      } else {
        setTracking(prev => ({
          ...prev,
          tokenValid: false,
          tokenValidated: true,
          validating: false,
          isIntruder: response.data.isIntruder || false,
          error: response.data.expired 
            ? 'Token expirado (máximo 8 horas)'
            : 'Token inválido o sesión no encontrada',
          connectionStatus: 'disconnected'
        }));
      }
    } catch (error: any) {
      console.error('[Tracking] Error validando token:', error);
      setTracking(prev => ({
        ...prev,
        tokenValid: false,
        tokenValidated: true,
        validating: false,
        error: 'Error al validar token: ' + (error.response?.data?.message || error.message),
        connectionStatus: 'disconnected'
      }));
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 🆕 CAPTURA DE FOTO DEL INTRUSO
  // ═══════════════════════════════════════════════════════════════════════════

  const captureIntruderPhoto = async (location: LocationData) => {
    try {
      console.log('[Intruso] Solicitando acceso a cámara...');
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      const video = document.createElement('video');
      video.srcObject = stream;
      video.setAttribute('playsinline', 'true');
      await video.play();
      
      // Esperar para ajuste de cámara
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(video, 0, 0);
      
      const photoData = canvas.toDataURL('image/jpeg', 0.8);
      
      stream.getTracks().forEach(track => track.stop());
      
      console.log('[Intruso] ✅ Foto capturada');
      
      sendIntruderAlert(location, photoData);
      
    } catch (error: any) {
      console.warn('[Intruso] No se pudo capturar foto:', error.message);
      
      sendIntruderAlert(location, null);
    } finally {
      setTracking(prev => ({ ...prev, capturingPhoto: false }));
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 🆕 ENVIAR ALERTA DE INTRUSIÓN
  // ═══════════════════════════════════════════════════════════════════════════

  const sendIntruderAlert = async (location: LocationData, photo: string | null) => {
    try {
      console.log('[Intruso] Enviando alerta al usuario original...');
      
      await axios.post(
        `${CONFIG.API_BASE_URL}/intrusion-alert`,
        {
          token: token,
          deviceFingerprint: deviceFingerprint,
          deviceInfo: getDeviceInfo(),
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy,
          photo: photo,
          timestamp: new Date().toISOString()
        },
        { timeout: 15000 }
      );
      
      console.log('[Intruso] ✅ Alerta enviada exitosamente');
      
      setTracking(prev => ({ ...prev, alertSent: true }));
      
    } catch (error: any) {
      console.error('[Intruso] Error enviando alerta:', error);
      
      setTracking(prev => ({ 
        ...prev, 
        alertSent: false,
        error: 'No se pudo enviar alerta de seguridad'
      }));
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // FUNCIONES API - TRACKING
  // ═══════════════════════════════════════════════════════════════════════════

  const startTracking = () => {
    if (tracking.isIntruder) {
      console.log('[Tracking] BLOQUEADO - dispositivo intruso');
      return;
    }

    if (CONFIG.USE_MOCK_LOCATION) {
      console.log('[Tracking] 🔧 MODO DESARROLLO - Mock Location');
      
      setTracking(prev => ({
        ...prev,
        active: true,
        error: null,
        permissionStatus: 'granted',
        connectionStatus: 'connected'
      }));

      const mockLocationData: LocationData = {
        ...CONFIG.MOCK_LOCATION,
        timestamp: new Date().toISOString()
      };
      
      setCurrentLocation(mockLocationData);
      sendLocationUpdate(mockLocationData);

      const mockInterval = setInterval(() => {
        const updatedMockLocation: LocationData = {
          ...CONFIG.MOCK_LOCATION,
          timestamp: new Date().toISOString()
        };
        setCurrentLocation(updatedMockLocation);
        sendLocationUpdate(updatedMockLocation);
      }, CONFIG.UPDATE_INTERVAL);

      watchIdRef.current = mockInterval as any;
      return;
    }

    if (!navigator.geolocation) {
      setTracking(prev => ({
        ...prev,
        error: 'Tu navegador no soporta geolocalización',
        permissionStatus: 'denied',
        connectionStatus: 'disconnected'
      }));
      return;
    }

    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    };

    const watchId = navigator.geolocation.watchPosition(
      handlePositionUpdate,
      handlePositionError,
      options
    );

    watchIdRef.current = watchId;

    setTracking(prev => ({
      ...prev,
      active: true,
      error: null,
      permissionStatus: 'granted',
      connectionStatus: 'connected'
    }));
  };

  const stopTracking = () => {
    if (watchIdRef.current !== null) {
      if (CONFIG.USE_MOCK_LOCATION) {
        clearInterval(watchIdRef.current as any);
      } else {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      watchIdRef.current = null;
    }

    setTracking(prev => ({
      ...prev,
      active: false,
      connectionStatus: 'disconnected'
    }));
  };

  const handlePositionUpdate = (position: GeolocationPosition) => {
    const locationData: LocationData = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      speed: position.coords.speed,
      timestamp: new Date().toISOString()
    };

    setCurrentLocation(locationData);
    sendLocationUpdate(locationData);
  };

  const handlePositionError = (error: GeolocationPositionError) => {
    console.error('[Tracking] Position error:', error);

    switch (error.code) {
      case error.PERMISSION_DENIED:
        setTracking(prev => ({
          ...prev,
          error: 'Permiso de ubicación denegado',
          active: false,
          permissionStatus: 'denied',
          connectionStatus: 'disconnected'
        }));
        break;
      case error.POSITION_UNAVAILABLE:
        setTracking(prev => ({
          ...prev,
          error: 'Ubicación no disponible',
          connectionStatus: 'waiting'
        }));
        break;
      case error.TIMEOUT:
        setTracking(prev => ({
          ...prev,
          error: 'Timeout obteniendo ubicación',
          connectionStatus: 'waiting'
        }));
        break;
    }
  };

  const sendLocationUpdate = async (location: LocationData) => {
    if (!token) return;

    setTracking(prev => ({ ...prev, sending: true }));

    try {
      const response = await axios.post(
        `${CONFIG.API_BASE_URL}/update`,
        {
          token,
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy,
          speed: location.speed || 0,
          timestamp: location.timestamp,
          deviceFingerprint: deviceFingerprint,
          deviceInfo: getDeviceInfo()
        },
        { timeout: 10000 }
      );

      if (response.data.success) {
        if (response.data.isIntruder) {
          setTracking(prev => ({
            ...prev,
            error: '🚨 Dispositivo no autorizado - sesión bloqueada',
            active: false,
            connectionStatus: 'disconnected'
          }));
          stopTracking();
          return;
        }
        
        setTracking(prev => ({
          ...prev,
          updateCount: prev.updateCount + 1,
          lastUpdate: new Date().toISOString(),
          error: null,
          sending: false,
          connectionStatus: 'connected'
        }));
      }
    } catch (error: any) {
      console.error('[Tracking] Error enviando ubicación:', error);
      
      if (error.response?.status === 403) {
        setTracking(prev => ({
          ...prev,
          error: '🚨 Dispositivo no autorizado',
          active: false,
          sending: false,
          connectionStatus: 'disconnected'
        }));
        stopTracking();
        return;
      }
      
      setTracking(prev => ({
        ...prev,
        error: '⚠️ Problema de conexión',
        sending: false,
        connectionStatus: 'waiting'
      }));
    }
  };

  const handleLogout = async () => {
  if (!token || !currentLocation) {
    console.log('[Tracking] Sin sesión activa, redirigiendo...');
    window.location.href = '/';
    return;
  }

  try {
    console.log('[Tracking] Cerrando sesión y expirando token...');
    
    // Enviar update final con flag de cierre
    await axios.post(
      `${CONFIG.API_BASE_URL}/update`,
      {
        token,
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude,
        accuracy: currentLocation.accuracy,
        speed: currentLocation.speed || 0,
        timestamp: new Date().toISOString(),
        deviceFingerprint: deviceFingerprint,
        deviceInfo: getDeviceInfo(),
        closing: true // 🆕 FLAG DE CIERRE
      },
      { timeout: 5000 }
    );
    
    console.log('[Tracking] ✅ Sesión cerrada exitosamente');
    
  } catch (error) {
    console.warn('[Tracking] Error cerrando sesión:', error);
    // No bloquear el cierre si falla
  } finally {
    stopTracking();
    
    // Pequeño delay para que el update llegue
    setTimeout(() => {
      window.location.href = '/';
    }, 500);
  }
};

  // ═══════════════════════════════════════════════════════════════════════════
  // SVG COMPONENTS
  // ═══════════════════════════════════════════════════════════════════════════

  const getWaspSVG = () => {
    return `
      <svg width="60" height="60" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <g transform="translate(50, 50) rotate(-45)">
          <ellipse cx="0" cy="5" rx="12" ry="20" fill="#FFC107" stroke="#000" stroke-width="2"/>
          <circle cx="0" cy="-12" r="8" fill="#FFC107" stroke="#000" stroke-width="2"/>
          <circle cx="2" cy="-12" r="2" fill="#000"/>
          <path d="M -2,-18 Q -5,-25 -3,-28" stroke="#000" stroke-width="1.5" fill="none"/>
          <path d="M 2,-18 Q 5,-25 3,-28" stroke="#000" stroke-width="1.5" fill="none"/>
          <line x1="-10" y1="0" x2="10" y2="0" stroke="#000" stroke-width="2"/>
          <line x1="-10" y1="8" x2="10" y2="8" stroke="#000" stroke-width="2"/>
          <ellipse cx="-8" cy="-5" rx="12" ry="6" fill="rgba(255,255,255,0.7)" stroke="#000" stroke-width="1" transform="rotate(-30 -8 -5)"/>
          <ellipse cx="8" cy="-5" rx="12" ry="6" fill="rgba(255,255,255,0.7)" stroke="#000" stroke-width="1" transform="rotate(30 8 -5)"/>
          <path d="M 0,22 L -2,28 L 0,32 L 2,28 Z" fill="#FF5722" stroke="#000" stroke-width="1"/>
        </g>
      </svg>
    `;
  };

  const getAngryWaspSVG = () => {
    return `
      <svg width="200" height="200" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        <g transform="translate(100, 100)">
          <ellipse cx="0" cy="10" rx="30" ry="50" fill="#FFC107" stroke="#000" stroke-width="3"/>
          <circle cx="0" cy="-30" r="20" fill="#FFC107" stroke="#000" stroke-width="3"/>
          
          <g>
            <ellipse cx="-8" cy="-32" rx="6" ry="8" fill="#000"/>
            <line x1="-14" y1="-38" x2="-4" y2="-34" stroke="#000" stroke-width="2" stroke-linecap="round"/>
            <ellipse cx="8" cy="-32" rx="6" ry="8" fill="#000"/>
            <line x1="4" y1="-34" x2="14" y2="-38" stroke="#000" stroke-width="2" stroke-linecap="round"/>
          </g>
          
          <path d="M -8,-22 Q 0,-18 8,-22" stroke="#000" stroke-width="2.5" fill="none" stroke-linecap="round"/>
          
          <path d="M -6,-48 L -10,-60 L -12,-64" stroke="#000" stroke-width="2.5" fill="none" stroke-linecap="round"/>
          <path d="M 6,-48 L 10,-60 L 12,-64" stroke="#000" stroke-width="2.5" fill="none" stroke-linecap="round"/>
          <circle cx="-12" cy="-64" r="3" fill="#dc3545"/>
          <circle cx="12" cy="-64" r="3" fill="#dc3545"/>
          
          <line x1="-25" y1="0" x2="25" y2="0" stroke="#000" stroke-width="4"/>
          <line x1="-25" y1="15" x2="25" y2="15" stroke="#000" stroke-width="4"/>
          <line x1="-25" y1="30" x2="25" y2="30" stroke="#000" stroke-width="4"/>
          
          <g filter="url(#glow)">
            <ellipse cx="-20" cy="-10" rx="25" ry="15" fill="rgba(135, 206, 250, 0.6)" stroke="#000" stroke-width="2" transform="rotate(-40 -20 -10)"/>
            <ellipse cx="20" cy="-10" rx="25" ry="15" fill="rgba(135, 206, 250, 0.6)" stroke="#000" stroke-width="2" transform="rotate(40 20 -10)"/>
          </g>
          
          <path d="M 0,55 L -4,65 L 0,75 L 4,65 Z" fill="#dc3545" stroke="#000" stroke-width="2" filter="url(#glow)"/>
          <circle cx="0" cy="75" r="3" fill="#8B0000"/>
          
          <g transform="translate(45, -20)">
            <circle cx="0" cy="0" r="25" fill="rgba(220, 53, 69, 0.2)" stroke="#dc3545" stroke-width="3"/>
            <line x1="-18" y1="-18" x2="18" y2="18" stroke="#dc3545" stroke-width="4" stroke-linecap="round"/>
          </g>
        </g>
      </svg>
    `;
  };

  const getFlyingWaspSVG = () => {
  return `
    <svg width="200" height="200" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="flying-glow">
          <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        
        <animateTransform 
          id="float-animation"
          attributeName="transform"
          type="translate"
          values="0,0; 0,-10; 0,0"
          dur="2s"
          repeatCount="indefinite"
        />
      </defs>
      
      <g transform="translate(100, 100)">
        <!-- Alas con brillo y movimiento -->
        <g filter="url(#flying-glow)">
          <ellipse cx="-20" cy="-10" rx="28" ry="14" 
                   fill="rgba(135, 206, 250, 0.7)" 
                   stroke="#000" stroke-width="2" 
                   transform="rotate(-35 -20 -10)">
            <animateTransform
              attributeName="transform"
              type="rotate"
              values="-35 -20 -10; -45 -20 -10; -35 -20 -10"
              dur="0.3s"
              repeatCount="indefinite"
            />
          </ellipse>
          
          <ellipse cx="20" cy="-10" rx="28" ry="14" 
                   fill="rgba(135, 206, 250, 0.7)" 
                   stroke="#000" stroke-width="2" 
                   transform="rotate(35 20 -10)">
            <animateTransform
              attributeName="transform"
              type="rotate"
              values="35 20 -10; 45 20 -10; 35 20 -10"
              dur="0.3s"
              repeatCount="indefinite"
            />
          </ellipse>
        </g>
        
        <!-- Cuerpo -->
        <ellipse cx="0" cy="10" rx="26" ry="45" fill="#FFC107" stroke="#000" stroke-width="3"/>
        
        <!-- Cabeza -->
        <circle cx="0" cy="-28" r="18" fill="#FFC107" stroke="#000" stroke-width="3"/>
        
        <!-- Ojos grandes y amigables -->
        <ellipse cx="-7" cy="-30" rx="5" ry="7" fill="#fff" stroke="#000" stroke-width="2"/>
        <circle cx="-7" cy="-29" r="3" fill="#000"/>
        
        <ellipse cx="7" cy="-30" rx="5" ry="7" fill="#fff" stroke="#000" stroke-width="2"/>
        <circle cx="7" cy="-29" r="3" fill="#000"/>
        
        <!-- Sonrisa amigable -->
        <path d="M -7,-20 Q 0,-17 7,-20" stroke="#000" stroke-width="2" fill="none" stroke-linecap="round"/>
        
        <!-- Antenas felices -->
        <g>
          <path d="M -5,-44 Q -8,-52 -6,-58" stroke="#000" stroke-width="2.5" fill="none" stroke-linecap="round"/>
          <circle cx="-6" cy="-58" r="3" fill="#FFC107">
            <animate attributeName="cy" values="-58; -60; -58" dur="1.5s" repeatCount="indefinite"/>
          </circle>
          
          <path d="M 5,-44 Q 8,-52 6,-58" stroke="#000" stroke-width="2.5" fill="none" stroke-linecap="round"/>
          <circle cx="6" cy="-58" r="3" fill="#FFC107">
            <animate attributeName="cy" values="-58; -60; -58" dur="1.5s" repeatCount="indefinite" begin="0.75s"/>
          </circle>
        </g>
        
        <!-- Rayas del cuerpo -->
        <line x1="-22" y1="0" x2="22" y2="0" stroke="#000" stroke-width="3"/>
        <line x1="-22" y1="15" x2="22" y2="15" stroke="#000" stroke-width="3"/>
        <line x1="-22" y1="30" x2="22" y2="30" stroke="#000" stroke-width="3"/>
        
        <!-- Aguijón -->
        <path d="M 0,52 L -3,60 L 0,66 L 3,60 Z" fill="#FFC107" stroke="#000" stroke-width="2"/>
        
        <!-- Ícono de ubicación flotante -->
        <g transform="translate(45, -15)" opacity="0.8">
          <circle cx="0" cy="0" r="22" fill="rgba(52, 199, 89, 0.2)" stroke="#34C759" stroke-width="2">
            <animate attributeName="r" values="22; 26; 22" dur="2s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.2; 0.4; 0.2" dur="2s" repeatCount="indefinite"/>
          </circle>
          
          <path d="M 0,-10 Q -8,-10 -8,0 Q -8,8 0,16 Q 8,8 8,0 Q 8,-10 0,-10 Z" 
                fill="#34C759" stroke="#000" stroke-width="1.5"/>
          <circle cx="0" cy="2" r="4" fill="#fff"/>
        </g>
        
        <!-- Animación del grupo completo -->
        <animateTransform
          attributeName="transform"
          type="translate"
          values="100,100; 100,90; 100,100"
          dur="2s"
          repeatCount="indefinite"
        />
      </g>
    </svg>
  `;
};

  const getConfusedWaspSVG = () => {
  return `
    <svg width="200" height="200" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="confused-glow">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      <g transform="translate(100, 100)">
        <!-- Cuerpo -->
        <ellipse cx="0" cy="10" rx="28" ry="48" fill="#FFC107" stroke="#000" stroke-width="3"/>
        <circle cx="0" cy="-28" r="18" fill="#FFC107" stroke="#000" stroke-width="3"/>
        
        <!-- Ojos confundidos (espirales) -->
        <g>
          <circle cx="-7" cy="-30" r="7" fill="#fff" stroke="#000" stroke-width="2"/>
          <path d="M -7,-30 Q -5,-32 -3,-30 Q -5,-28 -7,-30" fill="none" stroke="#000" stroke-width="1.5"/>
          
          <circle cx="7" cy="-30" r="7" fill="#fff" stroke="#000" stroke-width="2"/>
          <path d="M 7,-30 Q 9,-32 11,-30 Q 9,-28 7,-30" fill="none" stroke="#000" stroke-width="1.5"/>
        </g>
        
        <!-- Boca confundida (ondulada) -->
        <path d="M -6,-18 Q 0,-16 6,-18" stroke="#000" stroke-width="2" fill="none" stroke-linecap="round"/>
        
        <!-- Antenas caídas/confundidas -->
        <path d="M -5,-45 Q -8,-52 -6,-58" stroke="#000" stroke-width="2" fill="none" stroke-linecap="round"/>
        <path d="M 5,-45 Q 8,-52 6,-58" stroke="#000" stroke-width="2" fill="none" stroke-linecap="round"/>
        <circle cx="-6" cy="-58" r="3" fill="#6c757d"/>
        <circle cx="6" cy="-58" r="3" fill="#6c757d"/>
        
        <!-- Rayas del cuerpo -->
        <line x1="-23" y1="0" x2="23" y2="0" stroke="#000" stroke-width="3"/>
        <line x1="-23" y1="15" x2="23" y2="15" stroke="#000" stroke-width="3"/>
        <line x1="-23" y1="30" x2="23" y2="30" stroke="#000" stroke-width="3"/>
        
        <!-- Alas caídas (sin brillo) -->
        <ellipse cx="-18" cy="-8" rx="22" ry="12" fill="rgba(200, 200, 200, 0.5)" stroke="#000" stroke-width="2" transform="rotate(-50 -18 -8)"/>
        <ellipse cx="18" cy="-8" rx="22" ry="12" fill="rgba(200, 200, 200, 0.5)" stroke="#000" stroke-width="2" transform="rotate(50 18 -8)"/>
        
        <!-- Aguijón neutral -->
        <path d="M 0,53 L -3,62 L 0,68 L 3,62 Z" fill="#6c757d" stroke="#000" stroke-width="2"/>
        
        <!-- Signos de interrogación flotantes -->
        <g opacity="0.7" filter="url(#confused-glow)">
          <text x="-45" y="-20" font-size="32" fill="#ffc107" font-weight="bold">?</text>
          <text x="35" y="-25" font-size="28" fill="#ffc107" font-weight="bold">?</text>
          <text x="-35" y="50" font-size="24" fill="#ffc107" font-weight="bold">?</text>
        </g>
      </g>
    </svg>
  `;
};

  // ═══════════════════════════════════════════════════════════════════════════
  // ESTILOS APPLE AQUA
  // ═══════════════════════════════════════════════════════════════════════════

  const isMobile = window.innerWidth <= 768;

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      background: '#000000',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '0' : '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      position: 'relative',
      overflow: 'hidden'
    },
    mainCard: {
      background: 'rgba(255, 255, 255, 0.98)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderRadius: isMobile ? '0' : '20px',
      boxShadow: isMobile ? 'none' : '0 8px 32px rgba(255, 193, 7, 0.3)',
      maxWidth: isMobile ? '100%' : '480px',
      width: '100%',
      height: isMobile ? '100vh' : 'auto',
      overflow: 'hidden',
      border: isMobile ? 'none' : '2px solid #FFC107',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      padding: isMobile ? '16px' : '20px',
      borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
      background: 'linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexShrink: 0
    },
    headerTitle: {
      margin: 0,
      fontSize: isMobile ? '1rem' : '1.1rem',
      fontWeight: 600,
      color: '#1a1a1a',
      letterSpacing: '-0.3px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    content: {
      flex: 1,
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px',
      WebkitOverflowScrolling: 'touch'
    },
    // 🆕 Pantalla de permisos
    permissionScreen: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      textAlign: 'center',
      minHeight: '400px'
    },
    permissionTitle: {
      fontSize: '1.3rem',
      fontWeight: 700,
      color: '#1a1a1a',
      marginTop: '20px',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      justifyContent: 'center'
    },
    permissionMessage: {
      fontSize: '0.95rem',
      color: '#495057',
      lineHeight: '1.6',
      maxWidth: '380px',
      marginBottom: '24px'
    },
    permissionDetails: {
      background: 'rgba(255, 193, 7, 0.05)',
      border: '1px solid rgba(255, 193, 7, 0.2)',
      borderRadius: '12px',
      padding: '16px',
      fontSize: '0.85rem',
      color: '#6c757d',
      maxWidth: '380px',
      textAlign: 'left',
      marginBottom: '24px',
      lineHeight: '1.8'
    },
    permissionButton: {
      padding: '14px 32px',
      background: '#FFC107',
      color: '#000000',
      border: '2px solid #000000',
      borderRadius: '12px',
      fontWeight: 700,
      fontSize: '1rem',
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(255, 193, 7, 0.4)',
      transition: 'transform 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    mapContainer: {
      height: isMobile ? '50vh' : '320px',
      borderRadius: '12px',
      overflow: 'hidden',
      marginBottom: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      position: 'relative',
      border: '1px solid #e9ecef'
    },
    mapControls: {
      position: 'absolute',
      top: '10px',
      left: '10px',
      zIndex: 1000,
      display: 'flex',
      gap: '6px'
    },
    mapButton: {
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      border: 'none',
      borderRadius: '8px',
      padding: '8px 12px',
      fontSize: '0.75rem',
      fontWeight: 600,
      cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
      transition: 'all 0.2s',
      color: '#FFC107'
    },
    statusIndicator: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 12px',
      borderRadius: '10px',
      marginBottom: '12px',
      fontSize: '0.85rem',
      fontWeight: 500,
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)'
    },
    dataCard: {
      background: '#ffffff',
      backdropFilter: 'blur(10px)',
      borderRadius: '12px',
      padding: '12px',
      marginBottom: '12px',
      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)',
      border: '1px solid #e9ecef'
    },
    dataGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '10px'
    },
    dataCell: {
      padding: '10px',
      background: '#f8f9fa',
      borderRadius: '8px',
      border: '1px solid #e9ecef'
    },
    dataLabel: {
      fontSize: '0.7rem',
      color: '#FFC107',
      marginBottom: '4px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontWeight: 600
    },
    dataValue: {
      fontSize: '1rem',
      fontWeight: 700,
      color: '#212529',
      fontFamily: 'SF Mono, Monaco, monospace'
    },
    menuOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 9998,
      transition: 'opacity 0.3s',
      opacity: menuOpen ? 1 : 0,
      pointerEvents: menuOpen ? 'auto' : 'none'
    },
    sideMenu: {
      position: 'fixed',
      top: 0,
      left: 0,
      bottom: 0,
      width: '280px',
      background: '#ffffff',
      backdropFilter: 'blur(20px)',
      boxShadow: '2px 0 20px rgba(0, 0, 0, 0.1)',
      zIndex: 9999,
      transform: menuOpen ? 'translateX(0)' : 'translateX(-100%)',
      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      display: 'flex',
      flexDirection: 'column'
    },
    menuHeader: {
      padding: '20px',
      borderBottom: '1px solid #e9ecef',
      background: '#FFC107',
      color: '#000000'
    },
    menuItem: {
      padding: '16px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      cursor: 'pointer',
      transition: 'background 0.2s',
      borderBottom: '1px solid #e9ecef',
      color: '#212529',
      fontWeight: 500,
      fontSize: '0.95rem'
    },
    chatWidget: {
      position: 'fixed',
      bottom: isMobile ? '50px' : '20px',
      right: isMobile ? '20px' : '20px',
      width: chatOpen ? (isMobile ? 'calc(100vw - 40px)' : '340px') : '56px',
      height: chatOpen ? (isMobile ? '60vh' : '480px') : '56px',
      background: '#ffffff',
      backdropFilter: 'blur(20px)',
      borderRadius: chatOpen ? '16px' : '28px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      zIndex: 9997,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid #e9ecef'
    },
    chatButton: {
      width: '56px',
      height: '56px',
      borderRadius: '28px',
      background: '#FFC107',
      border: '2px solid #000000',
      color: '#000000',
      fontSize: '1.5rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 4px 12px rgba(255, 193, 7, 0.4)',
      transition: 'transform 0.2s'
    },
    chatHeader: {
      padding: '16px',
      background: '#FFC107',
      color: '#000000',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexShrink: 0
    },
    chatBody: {
      flex: 1,
      padding: '16px',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px'
    },
    chatMessage: {
      padding: '10px 14px',
      borderRadius: '12px',
      maxWidth: '85%',
      fontSize: '0.9rem',
      lineHeight: '1.4'
    },
    chatInput: {
      padding: '12px 16px',
      borderTop: '1px solid rgba(0, 0, 0, 0.06)',
      display: 'flex',
      gap: '8px',
      flexShrink: 0
    },
    pwaButton: {
      position: 'fixed',
      bottom: isMobile ? '20px' : '20px',
      left: '20px',
      padding: '8px 12px',
      background: '#ffffff',
      backdropFilter: 'blur(10px)',
      borderRadius: '20px',
      border: '1px solid #e9ecef',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      fontSize: '0.75rem',
      fontWeight: 600,
      color: '#FFC107',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      zIndex: 9996,
      opacity: showPWAPrompt ? 1 : 0,
      pointerEvents: showPWAPrompt ? 'auto' : 'none',
      transition: 'all 0.3s'
    },
    satelliteIcon: {
      fontSize: '1.2rem',
      transition: 'all 0.3s'
    },
    intruderScreen: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      textAlign: 'center',
      minHeight: '400px'
    },
    intruderIcon: {
      marginBottom: '30px',
      animation: 'shake 0.5s infinite'
    },
    intruderTitle: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#dc3545',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      justifyContent: 'center'
    },
    intruderMessage: {
      fontSize: '1rem',
      color: '#495057',
      lineHeight: '1.6',
      maxWidth: '400px',
      marginBottom: '30px'
    },
    intruderDetails: {
      background: 'rgba(220, 53, 69, 0.05)',
      border: '1px solid rgba(220, 53, 69, 0.2)',
      borderRadius: '12px',
      padding: '16px',
      fontSize: '0.85rem',
      color: '#6c757d',
      maxWidth: '400px',
      textAlign: 'left'
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER - COMPONENTES
  // ═══════════════════════════════════════════════════════════════════════════

  const renderPermissionRequest = () => {
  return (
    <div style={styles.permissionScreen}>
      <div 
        style={{ 
          marginBottom: '20px',
          animation: 'float 2s ease-in-out infinite'
        }} 
        dangerouslySetInnerHTML={{ __html: getFlyingWaspSVG() }} 
      />
      
      <h2 style={styles.permissionTitle}>
        <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: '#34C759' }} />
        Ubicación Requerida
      </h2>
      
      <p style={styles.permissionMessage}>
        WASP Tracker necesita acceso a tu ubicación GPS para funcionar correctamente, 
        incluso cuando la aplicación esté en segundo plano.
      </p>
      
      <div style={styles.permissionDetails}>
        <div style={{ marginBottom: '8px', fontWeight: 600, color: '#FFC107' }}>
          ✨ Características de Seguridad:
        </div>
        <div style={{ fontSize: '0.85rem', lineHeight: '1.8' }}>
          📍 Transmisión continua y segura<br />
          🔐 Protección con device fingerprinting<br />
          🚨 Alertas de intrusión automáticas<br />
          ⏱️ Válido por 8 horas<br />
          🔋 Optimizado para batería
        </div>
      </div>
      
      <button 
        onClick={handleRequestPermissions}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.05)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 193, 7, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 193, 7, 0.4)';
        }}
        style={{
          ...styles.permissionButton,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div style={{
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '100%',
          height: '100%',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
          animation: 'shimmer 2s infinite'
        }} />
        <FontAwesomeIcon icon={faLock} />
        Permitir Ubicación
      </button>
      
      <div style={{
        marginTop: '20px',
        fontSize: '0.75rem',
        color: '#999',
        textAlign: 'center',
        maxWidth: '320px'
      }}>
        🔒 Tu privacidad es importante. Solo el dispositivo autorizado puede acceder a tu ubicación.
      </div>
    </div>
  );
};

  const renderIntruderScreen = () => {
    return (
      <div style={styles.intruderScreen}>
        <div style={styles.intruderIcon} dangerouslySetInnerHTML={{ __html: getAngryWaspSVG() }} />
        
        <h2 style={styles.intruderTitle}>
          <FontAwesomeIcon icon={faBan} />
          ACCESO DENEGADO
        </h2>
        
        <p style={styles.intruderMessage}>
          Este enlace de tracking ya está siendo usado por otro dispositivo.<br />
          Por razones de seguridad, solo el dispositivo original puede acceder.
        </p>
        
        <div style={styles.intruderDetails}>
          <div style={{ marginBottom: '12px' }}>
            <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: '8px', color: '#dc3545' }} />
            <strong>Seguridad WASP</strong>
          </div>
          <div style={{ fontSize: '0.8rem', lineHeight: '1.6', marginBottom: '12px' }}>
            • Cada link está vinculado a un único dispositivo<br />
            • El primer dispositivo que abre el link queda registrado<br />
            • Esta medida protege tu privacidad y ubicación
          </div>
          {tracking.alertSent && (
            <div style={{ 
              marginTop: '12px', 
              padding: '8px', 
              background: 'rgba(40, 167, 69, 0.1)',
              borderRadius: '6px',
              fontSize: '0.8rem',
              color: '#28a745'
            }}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '6px' }} />
              El usuario original ha sido alertado de este intento
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderInvalidSessionScreen = () => {
  return (
    <div style={styles.intruderScreen}>
      <div 
        style={{ 
          ...styles.intruderIcon,
          animation: 'float 3s ease-in-out infinite'
        }} 
        dangerouslySetInnerHTML={{ __html: getConfusedWaspSVG() }} 
      />
      
      <h2 style={{
        ...styles.intruderTitle,
        color: '#ffc107'
      }}>
        <FontAwesomeIcon icon={faExclamationTriangle} />
        SESIÓN NO ENCONTRADA
      </h2>
      
      <p style={styles.intruderMessage}>
        Este enlace de tracking no es válido o ha expirado.<br />
        Los enlaces tienen una duración máxima de 8 horas.
      </p>
      
      <div style={{
        ...styles.intruderDetails,
        background: 'rgba(255, 193, 7, 0.05)',
        border: '1px solid rgba(255, 193, 7, 0.2)'
      }}>
        <div style={{ marginBottom: '12px' }}>
          <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '8px', color: '#ffc107' }} />
          <strong>Posibles causas</strong>
        </div>
        <div style={{ fontSize: '0.8rem', lineHeight: '1.6', marginBottom: '12px' }}>
          • El enlace ya expiró (máximo 8 horas)<br />
          • El enlace fue generado incorrectamente<br />
          • La sesión fue cerrada manualmente<br />
          • Token inválido o malformado
        </div>
        <div style={{ 
          marginTop: '16px', 
          padding: '10px', 
          background: 'rgba(255, 193, 7, 0.1)',
          borderRadius: '8px',
          fontSize: '0.85rem',
          color: '#856404',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <FontAwesomeIcon icon={faInfoCircle} />
          <span>Comparte nuevamente tu ubicación en WhatsApp para compartir tu ubicación en una nueva sesión.</span>
        </div>
      </div>
    </div>
  );
};

  const renderSatelliteStatus = () => {
    const getStatusColor = () => {
      switch (tracking.connectionStatus) {
        case 'connected': return '#28a745';
        case 'waiting': return '#ffc107';
        case 'disconnected': return '#dc3545';
        default: return '#6c757d';
      }
    };

    const getStatusText = () => {
      switch (tracking.connectionStatus) {
        case 'connected': return 'Transmitiendo';
        case 'waiting': return 'Esperando señal';
        case 'disconnected': return 'Sin señal';
        default: return 'Inicializando';
      }
    };

    return (
      <div style={{
        ...styles.statusIndicator,
        background: `rgba(${tracking.connectionStatus === 'connected' ? '40, 167, 69' : tracking.connectionStatus === 'waiting' ? '255, 193, 7' : '220, 53, 69'}, 0.1)`,
        border: `1px solid rgba(${tracking.connectionStatus === 'connected' ? '40, 167, 69' : tracking.connectionStatus === 'waiting' ? '255, 193, 7' : '220, 53, 69'}, 0.2)`
      }}>
        <FontAwesomeIcon 
          icon={faSatelliteDish} 
          style={{ 
            ...styles.satelliteIcon,
            color: getStatusColor(),
            animation: tracking.connectionStatus === 'connected' ? 'pulse 2s infinite' : 'none'
          }} 
        />
        <span style={{ color: getStatusColor(), fontWeight: 600 }}>
          {getStatusText()}
        </span>
        {tracking.active && tracking.connectionStatus === 'connected' && (
          <span style={{ 
            marginLeft: 'auto', 
            fontSize: '0.75rem', 
            color: '#6c757d',
            fontFamily: 'SF Mono, Monaco, monospace'
          }}>
            {tracking.updateCount} updates
          </span>
        )}
      </div>
    );
  };

  const renderChatbot = () => {
    if (!CONFIG.CHATBOT_ENABLED) return null;

    return (
      <div style={styles.chatWidget}>
        {!chatOpen ? (
          <button 
            style={styles.chatButton}
            onClick={() => setChatOpen(true)}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <FontAwesomeIcon icon={faRobot} />
          </button>
        ) : (
          <>
            <div style={styles.chatHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FontAwesomeIcon icon={faRobot} />
                <span style={{ fontWeight: 600 }}>Asistente WASP</span>
              </div>
              <button
                onClick={() => setChatOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '1.2rem'
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div style={styles.chatBody}>
              <div style={{
                ...styles.chatMessage,
                background: '#f8f9fa',
                border: '1px solid #e9ecef',
                alignSelf: 'flex-start',
                color: '#212529'
              }}>
                👋 ¡Hola! Soy tu asistente virtual. ¿En qué puedo ayudarte?
              </div>
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#6c757d',
                fontSize: '0.8rem',
                marginTop: 'auto'
              }}>
                💡 Chatbot desactivado
                <br />
                <small>Próximamente conectado a tu comunidad</small>
              </div>
            </div>

            <div style={styles.chatInput}>
              <input
                type="text"
                placeholder="Escribe tu mensaje..."
                disabled
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '20px',
                  outline: 'none',
                  fontSize: '0.9rem',
                  background: 'rgba(0, 0, 0, 0.02)'
                }}
              />
              <button
                disabled
                style={{
                  padding: '8px 16px',
                  background: '#FFC107',
                  color: '#000000',
                  border: '2px solid #000000',
                  borderRadius: '20px',
                  cursor: 'not-allowed',
                  opacity: 0.5,
                  fontWeight: 600,
                  fontSize: '0.85rem'
                }}
              >
                Enviar
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderSideMenu = () => {
    return (
      <>
        <div 
          style={styles.menuOverlay} 
          onClick={() => setMenuOpen(false)}
        />
        <div style={styles.sideMenu}>
          <div style={styles.menuHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div dangerouslySetInnerHTML={{ __html: getWaspSVG().replace('width="30"', 'width="32"').replace('height="30"', 'height="32"') }} />
              <div>
                <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700 }}>
                  WASP Tracker Menu
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', opacity: 0.8 }}>
                  Sistema de Emergencias
                </p>
              </div>
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            <div 
              style={styles.menuItem}
              onClick={() => {
                setMenuOpen(false);
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <FontAwesomeIcon icon={faHome} style={{ color: '#FFC107', fontSize: '1.1rem' }} />
              <span>Inicio</span>
            </div>

            <div 
              style={styles.menuItem}
              onClick={() => {
                setMenuOpen(false);
                handleLogout();
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(220, 53, 69, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <FontAwesomeIcon icon={faSignOutAlt} style={{ color: '#dc3545', fontSize: '1.1rem' }} />
              <span>Cerrar Sesión</span>
            </div>
          </div>

          <div style={{
            padding: '16px 20px',
            borderTop: '1px solid #e9ecef',
            fontSize: '0.75rem',
            color: '#6c757d',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '4px' }}>
              <FontAwesomeIcon icon={faLock} style={{ color: '#28a745' }} /> Conexión Segura
            </div>
            <div>WASP Tracker v2.1.1</div>
          </div>
        </div>
      </>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER PRINCIPAL
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div style={styles.container}>
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: tracking.isIntruder 
          ? 'radial-gradient(circle at 50% 50%, rgba(220, 53, 69, 0.05) 0%, transparent 50%)'
          : 'radial-gradient(circle at 20% 50%, rgba(255, 193, 7, 0.03) 0%, transparent 50%)',
        pointerEvents: 'none'
      }} />

      <div style={styles.mainCard}>
        <div style={styles.header}>
          <button
            onClick={() => setMenuOpen(true)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: tracking.tokenValid && !tracking.isIntruder ? 'pointer' : 'not-allowed',
              fontSize: '1.3rem',
              color: '#FFC107',
              padding: '8px',
              opacity: tracking.tokenValid && !tracking.isIntruder ? 1 : 0.3
            }}
            disabled={!tracking.tokenValid || tracking.isIntruder}
          >
            <FontAwesomeIcon icon={faBars} />
          </button>

          <h4 style={styles.headerTitle}>
            <div dangerouslySetInnerHTML={{ __html: getWaspSVG().replace('width="30"', 'width="24"').replace('height="20"', 'height="14"') }} />
            <span>WASP LinkHub</span>
          </h4>

          <div style={{ width: '40px' }} />
        </div>

        <div style={styles.content}>
          {/* ════════════════════════════════════════ */}
          {/* ESTADO 0: SOLICITAR PERMISOS            */}
          {/* ════════════════════════════════════════ */}
          {!tracking.permissionRequested && renderPermissionRequest()}

          {/* ════════════════════════════════════════ */}
          {/* ESTADO 1: PERMISOS DENEGADOS            */}
          {/* ════════════════════════════════════════ */}
          {tracking.permissionRequested && tracking.permissionStatus === 'denied' && (
            <div style={styles.permissionScreen}>
              <FontAwesomeIcon icon={faBan} style={{ fontSize: '60px', color: '#dc3545', marginBottom: '20px' }} />
              <h2 style={{ ...styles.permissionTitle, color: '#dc3545' }}>
                Permisos Requeridos
              </h2>
              <p style={styles.permissionMessage}>
                WASP necesita acceso a tu ubicación para funcionar.
                Por favor, recarga la página y permite el acceso.
              </p>
              <button 
                onClick={() => window.location.reload()}
                style={styles.permissionButton}
              >
                <FontAwesomeIcon icon={faSync} />
                Recargar Página
              </button>
            </div>
          )}

          {/* ════════════════════════════════════════ */}
          {/* ESTADO 2: VALIDANDO                     */}
          {/* ════════════════════════════════════════ */}
          {tracking.permissionRequested && tracking.validating && (
            <div style={{
              ...styles.statusIndicator,
              background: 'rgba(0, 123, 255, 0.1)',
              border: '1px solid rgba(0, 123, 255, 0.2)'
            }}>
              <FontAwesomeIcon icon={faSync} spin style={{ color: '#007bff' }} />
              <span style={{ color: '#007bff', fontWeight: 600 }}>Validando sesión...</span>
            </div>
          )}

          {/* ════════════════════════════════════════ */}
          {/* ESTADO 3: CAPTURANDO FOTO INTRUSO       */}
          {/* ════════════════════════════════════════ */}
          {tracking.isIntruder && tracking.capturingPhoto && (
            <div style={styles.permissionScreen}>
              <FontAwesomeIcon 
                icon={faCamera} 
                spin
                style={{ fontSize: '60px', color: '#dc3545', marginBottom: '20px' }} 
              />
              <h2 style={{ ...styles.permissionTitle, color: '#dc3545' }}>
                Capturando información de seguridad...
              </h2>
              <p style={styles.permissionMessage}>
                Por favor espera mientras recopilamos datos del intento de acceso.
              </p>
            </div>
          )}

          {/* ════════════════════════════════════════ */}
          {/* ESTADO 4: INTRUSO - PANTALLA FINAL      */}
          {/* ════════════════════════════════════════ */}
          {!tracking.validating && tracking.isIntruder && !tracking.capturingPhoto && renderIntruderScreen()}

          {/* ════════════════════════════════════════ */}
          {/* ESTADO 5: TOKEN INVÁLIDO                */}
          {/* ════════════════════════════════════════ */}
          {!tracking.validating && !tracking.isIntruder && !tracking.tokenValid && tracking.permissionRequested && renderInvalidSessionScreen()}
          
          {/* ════════════════════════════════════════ */}
          {/* ESTADO 6: TOKEN VÁLIDO - TRACKING       */}
          {/* ════════════════════════════════════════ */}
          {!tracking.validating && !tracking.isIntruder && tracking.tokenValid && (
            <>
              {renderSatelliteStatus()}

              {tracking.permissionStatus === 'denied' && (
                <div style={styles.dataCard}>
                  <div style={{
                    padding: '12px',
                    background: 'rgba(220, 53, 69, 0.1)',
                    borderRadius: '8px',
                    marginBottom: '12px'
                  }}>
                    <FontAwesomeIcon icon={faBan} style={{ color: '#dc3545', marginRight: '8px' }} />
                    <strong style={{ color: '#dc3545' }}>Permiso de Ubicación Denegado</strong>
                  </div>

                  <div style={{
                    padding: '12px',
                    background: 'rgba(0, 0, 0, 0.02)',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    lineHeight: '1.6'
                  }}>
                    <strong style={{ display: 'block', marginBottom: '8px', color: '#667eea' }}>
                      📱 Cómo activar:
                    </strong>
                    <ol style={{ margin: 0, paddingLeft: '20px' }}>
                      <li>Toca el ícono <FontAwesomeIcon icon={faLock} /> en la barra</li>
                      <li>Busca "Ubicación" o "Location"</li>
                      <li>Selecciona "Permitir"</li>
                      <li>Recarga la página</li>
                    </ol>
                  </div>

                  <button 
                    onClick={() => window.location.reload()} 
                    style={{
                      width: '100%',
                      marginTop: '12px',
                      padding: '12px',
                      background: '#FFC107',
                      color: '#000000',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      boxShadow: '0 2px 6px rgba(255, 193, 7, 0.3)'
                    }}
                  >
                    <FontAwesomeIcon icon={faSync} style={{ marginRight: '8px' }} />
                    Recargar Página
                  </button>
                </div>
              )}

              {tracking.active && tracking.permissionStatus === 'granted' && currentLocation && (
                <>
                  <div style={styles.mapContainer}>
                    <div style={styles.mapControls}>
                      <button
                        style={{
                          ...styles.mapButton,
                          background: mapType === 'street' ? '#FFC107' : 'rgba(255, 255, 255, 0.95)',
                          color: mapType === 'street' ? '#000000' : '#6c757d',
                          border: 'none'
                        }}
                        onClick={() => setMapType('street')}
                      >
                        <FontAwesomeIcon icon={faMap} style={{ marginRight: '4px' }} />
                        Calle
                      </button>
                      <button
                        style={{
                          ...styles.mapButton,
                          background: mapType === 'satellite' ? '#FFC107' : 'rgba(255, 255, 255, 0.95)',
                          color: mapType === 'satellite' ? '#000000' : '#6c757d',
                          border: 'none'
                        }}
                        onClick={() => setMapType('satellite')}
                      >
                        <FontAwesomeIcon icon={faGlobe} style={{ marginRight: '4px' }} />
                        Satélite
                      </button>
                      <button
                        style={{
                          ...styles.mapButton,
                          background: mapType === 'terrain' ? '#FFC107' : 'rgba(255, 255, 255, 0.95)',
                          color: mapType === 'terrain' ? '#000000' : '#6c757d',
                          border: 'none'
                        }}
                        onClick={() => setMapType('terrain')}
                      >
                        <FontAwesomeIcon icon={faMountain} style={{ marginRight: '4px' }} />
                        Terreno
                      </button>
                    </div>
                    <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
                  </div>

                  <div style={styles.dataCard}>
                    <div style={styles.dataGrid}>
                      <div style={styles.dataCell}>
                        <div style={styles.dataLabel}>Latitud</div>
                        <div style={styles.dataValue}>
                          {currentLocation.latitude.toFixed(6)}°
                        </div>
                      </div>

                      <div style={styles.dataCell}>
                        <div style={styles.dataLabel}>Longitud</div>
                        <div style={styles.dataValue}>
                          {currentLocation.longitude.toFixed(6)}°
                        </div>
                      </div>

                      <div style={styles.dataCell}>
                        <div style={styles.dataLabel}>Precisión</div>
                        <div style={styles.dataValue}>
                          ±{currentLocation.accuracy.toFixed(0)}m
                        </div>
                      </div>

                      <div style={styles.dataCell}>
                        <div style={styles.dataLabel}>Última Act.</div>
                        <div style={styles.dataValue}>
                          {tracking.lastUpdate 
                            ? new Date(tracking.lastUpdate).toLocaleTimeString('es-CL', {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })
                            : '--:--:--'
                          }
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{
                    padding: '10px 12px',
                    background: '#f8f9fa',
                    borderRadius: '10px',
                    fontSize: '0.8rem',
                    color: '#6c757d',
                    textAlign: 'center',
                    fontWeight: 500,
                    border: '1px solid #e9ecef'
                  }}>
                    <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '6px', color: '#FFC107' }} />
                    Actualización automática cada {CONFIG.UPDATE_INTERVAL / 1000}s • Válido 8h
                  </div>
                </>
              )}

              {tracking.error && tracking.permissionStatus !== 'denied' && !tracking.isIntruder && (
                <div style={{
                  padding: '10px 12px',
                  background: 'rgba(255, 193, 7, 0.1)',
                  border: '1px solid rgba(255, 193, 7, 0.2)',
                  borderRadius: '10px',
                  fontSize: '0.85rem',
                  color: '#ffc107',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>{tracking.error}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid #e9ecef',
          background: '#fafbfc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '0.75rem',
          flexShrink: 0
        }}>
          <span style={{ color: '#28a745', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <FontAwesomeIcon icon={faLock} />
            {tracking.isIntruder ? 'Acceso Bloqueado' : 'Conexión Segura'}
          </span>
          <span style={{ color: '#6c757d' }}>
            WASP Tracker v2.1.1
          </span>
        </div>
      </div>

      {!tracking.validating && tracking.tokenValid && !tracking.isIntruder && renderSideMenu()}
      {!tracking.validating && tracking.tokenValid && !tracking.isIntruder && renderChatbot()}

      {CONFIG.PWA_ENABLED && (
        <button style={styles.pwaButton} onClick={handleInstallPWA}>
          <FontAwesomeIcon icon={faDownload} />
          Instalar App
        </button>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.6;
            transform: scale(1.1);
          }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0) rotate(0deg); }
          25% { transform: translateX(-10px) rotate(-5deg); }
          75% { transform: translateX(10px) rotate(5deg); }
        }
        
        .leaflet-tile-pane {
          opacity: 1 !important;
        }
        
        .leaflet-fade-anim .leaflet-tile {
          will-change: auto !important;
          opacity: 1 !important;
        }
        
        .leaflet-zoom-anim .leaflet-zoom-animated {
          will-change: auto !important;
        }

        .wasp-marker {
          background: transparent !important;
          border: none !important;
        }

        ::-webkit-scrollbar {
          width: 6px;
        }

        ::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
        }

        ::-webkit-scrollbar-thumb {
          background: rgba(255, 193, 7, 0.2);
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 193, 7, 0.4);
        }

        @media all and (display-mode: standalone) {
          body {
            user-select: none;
            -webkit-user-select: none;
            -webkit-touch-callout: none;
          }
        }

        @keyframes float {
  0%, 100% { 
    transform: translateY(0) rotate(0deg); 
  }
  25% { 
    transform: translateY(-10px) rotate(-3deg); 
  }
  50% { 
    transform: translateY(-5px) rotate(3deg); 
  }
  75% { 
    transform: translateY(-15px) rotate(-2deg); 
  }
}

@keyframes questionMark {
  0%, 100% { opacity: 0.7; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.1); }
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

@keyframes float {
  0%, 100% { 
    transform: translateY(0); 
  }
  50% { 
    transform: translateY(-10px); 
  }
}
      `}</style>
    </div>
  );
};

export default TrackingPage;